import { useMemo } from 'react';

import { END_POINTS } from '../../../../../services';
import { TEXT_CONTENT } from '../../../../../utils/constants';
import {
  DATE_FORMAT,
  isValidDate,
  toDisplayDate,
} from '../../../../../utils/date';
import useGraphApiDataRevalidator from '../../../useGraphApiDataRevalidator';
import PdfUserParams, {
  ForcedShiftData,
  PdfUserParamsProps,
} from './PdfUserParams';

export const usePdfUserParams = ({
  facility,
  scenarioLabel,
}: {
  facility: string;
  scenarioLabel: string;
}) => {
  const { data: defaultUserParamData = {}, isLoading } =
    useGraphApiDataRevalidator(END_POINTS.SOT.getUserParams(facility));

  const pdfData = useMemo(() => {
    const appShiftLengths =
      (defaultUserParamData?.facility_app_shift_length || [])
        ?.map?.((d: any) => `${d} Hours`)
        .join(',') || '-';
    const physicianShiftLengths =
      (defaultUserParamData?.facility_physician_shift_length || [])
        ?.map?.((d: any) => `${d} Hours`)
        .join(',') || '-';
    const startDate = isValidDate(defaultUserParamData?.StartDate)
      ? toDisplayDate(defaultUserParamData?.StartDate, DATE_FORMAT.DATE_ONLY)
      : '-';
    const endDate = isValidDate(defaultUserParamData?.EndDate)
      ? toDisplayDate(defaultUserParamData?.EndDate, DATE_FORMAT.DATE_ONLY)
      : '-';

    let newUserParam: PdfUserParamsProps['data'] = {
      facility: { title: 'Facility', values: [facility] },
      scenario: {
        title: TEXT_CONTENT.SHIFT_MAP_OPTIONS,
        values: [scenarioLabel],
      },
      // enableOverrideValue: {title:'Enable Override' values:['false']},
      patientVolume: {
        title: 'Patient Volume',
        values: [
          `Patient Volume : ${defaultUserParamData?.TotalCasesDailyAvgOverride || '-'}`,
        ],
      },
      arrivalCubeData: {
        title: 'DOS Range',
        values: [`From : ${startDate}`, `To : ${endDate}`],
      },
      phyAppBaseline: {
        title: 'Physician and App Patients/Hour',
        values: [
          `Physician Cases Per Hour Baseline : ${defaultUserParamData?.PhyCasesPerHourBaseline ?? '-'}`,
          `APP Cases Per Hour Baseline : ${defaultUserParamData?.APPCasesPerHourBaseline ?? '-'}`,
        ],
      },
      shiftLengths: {
        title: TEXT_CONTENT.SHIFT_LENGTHS,
        values: [
          `Physician : ${physicianShiftLengths}`,
          `APP : ${appShiftLengths}`,
        ],
      },
      pctCasesAppAuto: {
        title: 'APP % Visits(% of cases APP Autonomous)',
        values: [
          `99281:${
            defaultUserParamData?.PctOfCases99281APPAuto !== '' &&
            !isNaN(defaultUserParamData?.PctOfCases99281APPAuto * 100)
              ? Math.round(defaultUserParamData?.PctOfCases99281APPAuto * 100)
              : '-'
          }`,
          `99282:${
            defaultUserParamData?.PctOfCases99282APPAuto !== '' &&
            !isNaN(defaultUserParamData?.PctOfCases99282APPAuto * 100)
              ? Math.round(defaultUserParamData?.PctOfCases99282APPAuto * 100)
              : '-'
          }`,
          `99283:${
            defaultUserParamData?.PctOfCases99283APPAuto !== '' &&
            !isNaN(defaultUserParamData?.PctOfCases99283APPAuto * 100)
              ? Math.round(defaultUserParamData?.PctOfCases99283APPAuto * 100)
              : '-'
          }`,
          `99284:${
            defaultUserParamData?.PctOfCases99284APPAuto !== '' &&
            !isNaN(defaultUserParamData?.PctOfCases99284APPAuto * 100)
              ? Math.round(defaultUserParamData?.PctOfCases99284APPAuto * 100)
              : '-'
          }`,
          `99285:${
            defaultUserParamData?.PctOfCases99285APPAuto !== '' &&
            !isNaN(defaultUserParamData?.PctOfCases99285APPAuto * 100)
              ? Math.round(defaultUserParamData?.PctOfCases99285APPAuto * 100)
              : '-'
          }`,
          `99291:${
            defaultUserParamData?.PctOfCases99291APPAuto !== '' &&
            !isNaN(defaultUserParamData?.PctOfCases99291APPAuto * 100)
              ? Math.round(defaultUserParamData?.PctOfCases99291APPAuto * 100)
              : '-'
          }`,
        ],
      },

      shiftStartTime: {
        title: 'Shift Start Time',
        values: [
          `Sunday Start Hour of Day : ${defaultUserParamData?.shift_start_time || '-'}`,
        ],
      },
    };
    let forcedShiftCollection: ForcedShiftData[] =
      defaultUserParamData?.forced_shift
        ?.map?.((d: any, index: number) => {
          const startDt = new Date(d?.DateHour || '');
          const shiftLength = d?.ShiftLength || 0;
          const endDt = new Date(d?.DateHour || '');
          endDt.setHours(endDt.getHours() + shiftLength, 0, 0, 0);
          return {
            id: String(++index),
            shiftStartDateHour: startDt?.toISOString(),
            shiftEndDateHour: endDt?.toISOString(),
            providerType: d?.ProviderTypeName || '',
            shiftLength: Number(shiftLength),
          };
        })
        ?.sort((a: ForcedShiftData, b: ForcedShiftData) => {
          const startDateA = new Date(a.shiftStartDateHour);
          const startDateB = new Date(b.shiftStartDateHour);
          if (a.providerType === b.providerType) {
            return (
              new Date(startDateA).valueOf() - new Date(startDateB).valueOf()
            );
          } else if (a.providerType?.toLowerCase() === 'physician') return -1;
          else if (a.providerType?.toLowerCase() === 'app') return 1;
          return 0;
        }) || [];

    return { data: newUserParam, forcedShifts: forcedShiftCollection || [] };
  }, [defaultUserParamData, facility, scenarioLabel]);

  return {
    renderer: PdfUserParams({
      data: pdfData.data,
      forcedShifts: pdfData?.forcedShifts || [],
    }),
    isLoading,
  };
};
export default usePdfUserParams;
